import type { CouponByBrand } from '@/api/worldCup';
import { ChartCard } from '../ui/ChartCard';
import styles from './style.module.css';

/*
 * Per-brand reward usage. Visual language mirrors TopPrizesList — one row per
 * brand, bar length proportional to that brand's TOTAL relative to the largest.
 *
 * Two row kinds:
 *   - coupon (default): three same-hue segments encode the email / in-store /
 *     not-used split via shade (full → mid → pale of the brand colour).
 *   - credit (e.g. Carrefour ChequeAhorro): NOT a coupon, so it has no channel
 *     split — rendered as a single solid bar with a small "credit" tag, so it
 *     still appears in the same table alongside the coupon brands.
 *
 * Backend ships rows sorted by total volume desc; we respect that order. Brand
 * colour comes from `brandColors[brand_id]` — same source used elsewhere.
 *
 * Tooltips: native `title` attribute. Keeps the component dependency-free.
 */
type Props = {
  rows: CouponByBrand[];
  brandColors: Record<string, string>;
};

const numFmt = new Intl.NumberFormat('en-US');

function v(c?: { value: number }): number {
  return c?.value ?? 0;
}

function d(c?: { display: string; value: number }): string {
  if (!c) return '0';
  return c.display ?? numFmt.format(c.value);
}

// Brand total: prefer the backend's authoritative `total`, else sum the three
// channel buckets (older payloads without `total`).
function rowTotal(r: CouponByBrand): number {
  return r.total ? v(r.total) : v(r.used_email) + v(r.used_in_store) + v(r.not_used);
}

// Mix a hex colour with var(--paper) using CSS color-mix(). The browser does
// the math at paint time, so we don't need a hex→rgb util. `pct` is how much
// of the brand colour to keep (100 = solid, 0 = paper).
function tint(brand: string, pct: number): string {
  return `color-mix(in srgb, ${brand} ${pct}%, var(--paper))`;
}

export function CouponsByBrandTable({ rows, brandColors }: Props) {
  if (rows.length === 0) return null;

  // Largest brand-total drives the proportional bar length. Falls back to 1
  // so an all-zero data set still renders an empty (legible) grid.
  const max = Math.max(1, ...rows.map(rowTotal));

  return (
    <ChartCard title="Rewards by brand">
      <div className={styles.legend} aria-hidden>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: tint('var(--ink)', 100) }} /> Used via email
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: tint('var(--ink)', 55) }} /> Used in store
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: tint('var(--ink)', 22) }} /> Not used yet
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendSwatch} style={{ background: tint('var(--ink)', 70) }} /> Credit (no channel)
        </span>
      </div>
      <ul className={styles.list}>
        {rows.map((r) => {
          const total = rowTotal(r);
          const brand = brandColors[r.brand_id] ?? 'var(--ink)';
          const barWidth = `${(total / max) * 100}%`;
          const isCredit = r.kind === 'credit';

          return (
            <li key={r.brand_id} className={styles.row}>
              <span className={`${styles.name} mono`}>
                {r.name}
                {isCredit && <span className={styles.tag}>credit</span>}
              </span>
              <div className={styles.track} aria-hidden>
                {isCredit ? (
                  // Credit: one solid bar — no channel split exists.
                  <div className={styles.bar} style={{ width: barWidth }} title={`Credit rewards: ${d(r.total)}`}>
                    <div className={styles.seg} style={{ width: '100%', background: tint(brand, 70) }} />
                  </div>
                ) : (
                  <div className={styles.bar} style={{ width: barWidth }}>
                    <div
                      className={styles.seg}
                      style={{ width: total > 0 ? `${(v(r.used_email) / total) * 100}%` : '0%', background: tint(brand, 100) }}
                      title={`Used via email: ${d(r.used_email)}`}
                    />
                    <div
                      className={styles.seg}
                      style={{ width: total > 0 ? `${(v(r.used_in_store) / total) * 100}%` : '0%', background: tint(brand, 55) }}
                      title={`Used in store: ${d(r.used_in_store)}`}
                    />
                    <div
                      className={styles.seg}
                      style={{ width: total > 0 ? `${(v(r.not_used) / total) * 100}%` : '0%', background: tint(brand, 22) }}
                      title={`Not used yet: ${d(r.not_used)}`}
                    />
                  </div>
                )}
              </div>
              <span className={`${styles.value} mono`}>{numFmt.format(total)}</span>
            </li>
          );
        })}
      </ul>
    </ChartCard>
  );
}
